/**
 * Category budget status - Shows budget usage by category
 */

import { useMemo } from 'react';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { BudgetProgressBar } from '../../../components/primitives';
import { formatCurrency } from '../../../lib/utils/format';
import { useCompanyCurrency } from '../../../hooks/useCompanyCurrency';
import { useBudgetTracking } from '../../../hooks/useBudgetTracking';
import { Skeleton } from '../../../components/ui/skeleton';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

interface CategoryBudgetStatusProps {
  companyIds: string[];
  userId: string;
  limit?: number;
  onNavigateToBudget?: () => void;
}

export function CategoryBudgetStatus({ 
  companyIds, 
  userId,
  limit = 5,
  onNavigateToBudget
}: CategoryBudgetStatusProps) {
  const companyCurrency = useCompanyCurrency();
  const { data, loading, hasActiveBudget } = useBudgetTracking(companyIds, userId);

  const topCategories = useMemo(() => {
    if (!data) return [];
    return data.categories.slice(0, limit);
  }, [data, limit]);

  const alertCount = useMemo(() => {
    if (!data) return 0;
    return data.categories.filter(c => c.status === 'danger' || c.status === 'over').length;
  }, [data]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-56" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!hasActiveBudget || !data) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle>Status por Categoria</CardTitle>
          {alertCount > 0 && (
            <Badge variant="destructive" className="gap-1 shrink-0 text-xs">
              <AlertCircle className="h-3 w-3" />
              {alertCount} {alertCount === 1 ? 'alerta' : 'alertas'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {topCategories.map((category) => (
          <div key={category.categoryId} className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm truncate">{category.categoryName}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                {formatCurrency(category.spent, companyCurrency)} / {formatCurrency(category.budgeted, companyCurrency)}
              </span>
            </div>
            <BudgetProgressBar
              current={category.spent}
              total={category.budgeted}
              showLabel={false}
              showIcon={false}
              size="sm"
            />
            {category.isOverBudget && (
              <p className="text-xs text-danger-600 dark:text-danger-400">
                Ultrapassou em {formatCurrency(category.spent - category.budgeted, companyCurrency)}
              </p>
            )}
          </div>
        ))}

        {data.categories.length > limit && (
          <Button variant="ghost" size="sm" className="w-full gap-2" onClick={onNavigateToBudget}>
            Ver todas as categorias
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
